import { useState } from 'react'
import { ApiError } from '../api/client'
import {
  useAddCensusEntry,
  useBedTypes,
  useCensusLedger,
  useCostCenters,
  useDeleteCensusEntry,
  useOrganizationSettings,
} from '../api/hooks'
import type { CensusEntryType } from '../api/types'
import { formatDate } from './formatTime'

const ENTRY_TYPES: { value: CensusEntryType; label: string; hint: string }[] = [
  {
    value: 'Baseline',
    label: 'Starting occupancy',
    hint: 'The occupied beds this ledger starts from. One per department and bed type, and nothing may precede it.',
  },
  {
    value: 'Change',
    label: 'Change',
    hint: 'A movement on the day it happened: +2 admissions, -1 discharge.',
  },
  {
    value: 'Recount',
    label: 'Recount',
    hint: 'An absolute figure that replaces the running total from this date on. For when the count has drifted and somebody has walked the floor.',
  },
]

/**
 * The occupancy ledger (PPD mode).
 *
 * A ledger of what CHANGED rather than a figure per day. A unit whose census moves twice
 * a month gets two rows, not thirty — and a day nobody typed anything for is not missing
 * data, it is a day the census did not change.
 */
export function CensusPanel() {
  const { data: settings } = useOrganizationSettings()
  const ppdEnabled = settings?.ppdModeEnabled ?? false

  const { data: costCenters = [] } = useCostCenters('flat', false)
  const { data: bedTypes = [] } = useBedTypes(ppdEnabled)

  const [costCenterId, setCostCenterId] = useState<number | null>(null)
  const [bedTypeId, setBedTypeId] = useState<number | null>(null)

  const { data: entries = [] } = useCensusLedger(
    costCenterId,
    bedTypeId,
    ppdEnabled && costCenterId !== null && bedTypeId !== null,
  )

  const add = useAddCensusEntry()
  const remove = useDeleteCensusEntry()

  const [entryType, setEntryType] = useState<CensusEntryType>('Baseline')
  const [effectiveDate, setEffectiveDate] = useState('')
  const [beds, setBeds] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  if (!ppdEnabled) {
    return null
  }

  const active = bedTypes.filter((b) => b.isActive)

  // A ledger that has no starting occupancy can only take one thing next, so the form
  // says so rather than letting somebody submit a change and read a refusal.
  const hasBaseline = entries.some((e) => e.entryType === 'Baseline')
  const selectionMade = costCenterId !== null && bedTypeId !== null

  function submit() {
    setError(null)

    const count = Number(beds)

    if (!effectiveDate || Number.isNaN(count)) {
      setError('A date and a number of beds are both required.')
      return
    }

    if (entryType === 'Change' && count === 0) {
      setError('A change of zero is a row saying nothing happened. Leave it out.')
      return
    }

    if (entryType !== 'Change' && count < 0) {
      setError('An occupied bed count cannot be negative.')
      return
    }

    add.mutate(
      {
        costCenterId: costCenterId!,
        bedTypeId: bedTypeId!,
        effectiveDate,
        entryType,
        beds: count,
        note: note.trim() === '' ? null : note.trim(),
      },
      {
        onSuccess: () => {
          setBeds('')
          setNote('')
          // The date is deliberately kept: entering a run of movements for one day is the
          // common case, and re-typing the date every time is the sort of friction that
          // gets a ledger abandoned.
        },
        onError: (e) =>
          setError(e instanceof ApiError ? e.message : 'The entry could not be saved.'),
      },
    )
  }

  return (
    <section className="border-t" style={{ borderColor: 'var(--color-rule)' }}>
      <div
        className="flex flex-wrap items-center gap-3 px-4 py-2"
        style={{ background: 'var(--color-panel-alt)' }}
      >
        <h2 className="col-head">occupancy</h2>
        <span className="text-[11px]" style={{ color: 'var(--color-faint)' }}>
          Record the starting occupancy once, then only what changes. Patient days are the
          sum of the midnight census over the period.
        </span>
      </div>

      <div
        className="flex flex-wrap items-end gap-3 border-b px-4 py-2"
        style={{ borderColor: 'var(--color-rule-soft)' }}
      >
        <label className="flex flex-col gap-1 text-[12px]">
          <span className="col-head">department</span>
          <select
            className="input w-64"
            value={costCenterId ?? ''}
            onChange={(e) => setCostCenterId(e.target.value === '' ? null : Number(e.target.value))}
          >
            <option value="">Choose…</option>
            {costCenters.map((c) => (
              <option key={c.costCenterId} value={c.costCenterId}>
                {c.code} — {c.name}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-[12px]">
          <span className="col-head">bed type</span>
          <select
            className="input w-48"
            value={bedTypeId ?? ''}
            onChange={(e) => setBedTypeId(e.target.value === '' ? null : Number(e.target.value))}
          >
            <option value="">Choose…</option>
            {active.map((b) => (
              <option key={b.bedTypeId} value={b.bedTypeId}>
                {b.name}
              </option>
            ))}
          </select>
        </label>

        {active.length === 0 && (
          <span className="text-[12px]" style={{ color: 'var(--color-warn)' }}>
            No bed types yet. An administrator adds them before occupancy can be recorded.
          </span>
        )}
      </div>

      {!selectionMade ? (
        <p className="px-4 py-3 text-[12px]" style={{ color: 'var(--color-faint)' }}>
          Choose a department and bed type to see and record its occupancy.
        </p>
      ) : (
        <>
          <div
            className="flex flex-wrap items-end gap-3 border-b px-4 py-3"
            style={{ borderColor: 'var(--color-rule-soft)' }}
          >
            <label className="flex flex-col gap-1 text-[12px]">
              <span className="col-head">entry</span>
              <select
                className="input w-48"
                value={entryType}
                onChange={(e) => setEntryType(e.target.value as CensusEntryType)}
              >
                {ENTRY_TYPES.map((t) => (
                  <option
                    key={t.value}
                    value={t.value}
                    // The starting occupancy exists once. Offering it again would produce
                    // a refusal the user could have been spared.
                    disabled={t.value === 'Baseline' && hasBaseline}
                  >
                    {t.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-[12px]">
              <span className="col-head">date</span>
              <input
                type="date"
                className="input w-40"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
              />
            </label>

            <label className="flex flex-col gap-1 text-[12px]">
              <span className="col-head">{entryType === 'Change' ? 'change' : 'beds'}</span>
              <input
                type="number"
                className="input w-24"
                value={beds}
                placeholder={entryType === 'Change' ? '+2' : '8'}
                onChange={(e) => setBeds(e.target.value)}
              />
            </label>

            <label className="flex flex-col gap-1 text-[12px]">
              <span className="col-head">note</span>
              <input
                className="input w-64"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={entryType === 'Recount' ? 'Who counted, and when' : ''}
              />
            </label>

            <button className="btn btn-sm" onClick={submit} disabled={add.isPending}>
              {add.isPending ? 'Saving…' : 'Record'}
            </button>

            <span className="text-[11px]" style={{ color: 'var(--color-faint)', maxWidth: '22rem' }}>
              {ENTRY_TYPES.find((t) => t.value === entryType)?.hint}
            </span>
          </div>

          {error && (
            <p
              className="px-4 py-1.5 text-[12px]"
              style={{ background: 'var(--color-error-wash)', color: 'var(--color-error)' }}
              role="alert"
            >
              {error}
            </p>
          )}

          {!hasBaseline && (
            <p className="px-4 py-1.5 text-[12px]" style={{ color: 'var(--color-warn)' }}>
              This department and bed type has no starting occupancy yet. Record it first —
              a change has nothing to be relative to without one.
            </p>
          )}

          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b text-left" style={{ borderColor: 'var(--color-rule)' }}>
                <th className="col-head px-4 py-1.5">Date</th>
                <th className="col-head px-4 py-1.5">Entry</th>
                <th className="col-head px-4 py-1.5 text-right">Beds</th>
                <th className="col-head px-4 py-1.5">Note</th>
                <th className="col-head px-4 py-1.5">Recorded by</th>
                <th className="col-head px-4 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr
                  key={e.censusEntryId}
                  className="border-b"
                  style={{ borderColor: 'var(--color-rule-soft)' }}
                >
                  <td className="ident px-4 py-1.5">{formatDate(e.effectiveDate)}</td>
                  <td className="px-4 py-1.5 text-[12px]">
                    {ENTRY_TYPES.find((t) => t.value === e.entryType)?.label ?? e.entryType}
                  </td>
                  <td className="ident px-4 py-1.5 text-right">
                    {/* A delta reads as a delta. "2" and "+2" mean different things here. */}
                    {e.entryType === 'Change' && e.beds > 0 ? `+${e.beds}` : e.beds}
                  </td>
                  <td className="px-4 py-1.5 text-[12px]" style={{ color: 'var(--color-muted)' }}>
                    {e.note}
                  </td>
                  <td className="px-4 py-1.5 text-[12px]" style={{ color: 'var(--color-muted)' }}>
                    {e.createdBy}
                  </td>
                  <td className="px-4 py-1.5 text-right">
                    <button
                      className="text-[12px] underline"
                      style={{ color: 'var(--color-error)' }}
                      onClick={() => remove.mutate(e.censusEntryId)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}

              {entries.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-3 text-[12px]"
                    style={{ color: 'var(--color-faint)' }}
                  >
                    Nothing recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </section>
  )
}
